import { resolvePublicPath } from './publicPath';

export function normalizeApiBase(raw: string): string {
  let b = raw.trim().replace(/\/+$/, '');
  if (b.endsWith('/api')) {
    b = b.slice(0, -4).replace(/\/+$/, '');
  }
  return b;
}

export const API_BASE = normalizeApiBase(
  process.env.REACT_APP_API_BASE || resolvePublicPath() || '',
);

/** Origin + optional org path prefix for WebSocket URLs. */
export function resolveApiOrigin(): string {
  if (!API_BASE) return window.location.origin;
  if (/^https?:\/\//i.test(API_BASE)) return API_BASE;
  const prefix = API_BASE.startsWith('/') ? API_BASE : `/${API_BASE}`;
  return `${window.location.origin}${prefix}`;
}

/** Limbic API base — auth is LOCKED to this remote host; local/relative is never allowed. */
export const LIMBIC_API_DEFAULT_BASE_URL = '';

/**
 * True for empty, relative/schemeless, or localhost/loopback URLs.
 * These must never be used for auth — they would resolve to the app's own origin
 * (e.g. http://localhost:8081/auth/api/auth/login) instead of the Limbic API.
 */
export function isLocalApiUrl(raw: string): boolean {
  const v = raw.trim().toLowerCase();
  if (!v) return true;
  if (!/^https?:\/\//.test(v)) return true; // relative/schemeless → resolves to local origin
  const host = v.replace(/^https?:\/\//, '').split('/')[0];
  return /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[?::1\]?)(:\d+)?$/.test(host);
}

function resolveAuthLoginEnv(): string {
  // Read only LIMBIC_API_BASE_URL — normalizeAuthLoginUrl derives /auth/api/auth/login.
  const fromEnv = (process.env.REACT_APP_LIMBIC_API_BASE_URL || '').trim();
  if (fromEnv && !isLocalApiUrl(fromEnv)) return fromEnv;
  if (fromEnv && isLocalApiUrl(fromEnv)) {
    // eslint-disable-next-line no-console
    console.warn(
      `[auth] REACT_APP_LIMBIC_API_BASE_URL="${fromEnv}" is local — coercing to ${LIMBIC_API_DEFAULT_BASE_URL} (local not allowed)`,
    );
  }
  return LIMBIC_API_DEFAULT_BASE_URL;
}

export function normalizeAuthLoginUrl(raw: string): string {
  const b = raw.trim().replace(/\/+$/, '');
  if (!b) return '';
  if (b.endsWith('/auth/api/auth/login') || b.endsWith('/api/auth/login')) return b;
  if (b.endsWith('/auth/api/auth') || b.endsWith('/api/auth')) return `${b}/login`;
  if (b.endsWith('/auth')) return `${b}/api/auth/login`;
  return `${b}/auth/api/auth/login`;
}

export const AUTH_LOGIN_URL = normalizeAuthLoginUrl(resolveAuthLoginEnv());

/**
 * Maker (object-library) API base — same Limbic origin as auth, under /maker.
 * Derived from AUTH_LOGIN_URL origin so it tracks UAT/prod without extra env.
 */
export function resolveMakerApiBase(): string {
  const fromEnv = (process.env.REACT_APP_MAKER_API_BASE || '').trim().replace(/\/+$/, '');
  if (fromEnv) return fromEnv;
  let origin = '';
  if (AUTH_LOGIN_URL && /^https?:\/\//i.test(AUTH_LOGIN_URL)) {
    try {
      origin = new URL(AUTH_LOGIN_URL).origin;
    } catch {
      origin = '';
    }
  }
  if (!origin && typeof window !== 'undefined') origin = window.location.origin;
  return `${origin}/maker`;
}

export const MAKER_API_BASE = resolveMakerApiBase();

export function getWsChatUrl(): string {
  const base = resolveApiOrigin();
  const wsBase = base.replace(/^https?/, (p) => (p === 'https' ? 'wss' : 'ws')) + '/api/v2/ws/chat';

  // Get auth token from localStorage and append as query parameter
  try {
    const token = localStorage.getItem('admin_token') || '';
    if (token) {
      return `${wsBase}?token=${encodeURIComponent(token)}`;
    }
  } catch {
    // localStorage not accessible (SSR, private mode, etc)
  }

  return wsBase;
}

export const CHATBOT_API_BASE = normalizeApiBase(
  process.env.REACT_APP_CHATBOT_API_BASE || process.env.REACT_APP_API_BASE || '',
);

export const DEFAULT_ORG_NAME = (process.env.REACT_APP_DEFAULT_ORG_NAME || '').trim();

/** Comma-separated org names allowed to use master chat (e.g. REACT_APP_MASTER_CHAT_ORGS=limbic). */
export function masterChatAllowedOrgs(): Set<string> {
  const raw = (process.env.REACT_APP_MASTER_CHAT_ORGS || '').trim();
  if (!raw) return new Set();
  return new Set(
    raw
      .split(',')
      .map((s) => s.trim().toLowerCase())
      .filter(Boolean),
  );
}

export function isMasterChatOrg(orgName: string): boolean {
  const allowed = masterChatAllowedOrgs();
  if (allowed.size === 0) return false;
  return allowed.has(orgName.trim().toLowerCase());
}

export const ENDPOINTS = {
  /** Agent Station session login (username/password only). */
  asAuthLogin: '/api/v2/auth/login',
  asAuthSession: '/api/v2/auth/session',
  asAuthLogout: '/api/v2/auth/logout',
  asAuthExchangeOrg: '/api/v2/auth/exchange-org',
  adminOrganizationsLimbic: '/api/v2/admin/organizations/limbic',
  adminOrganizations: '/api/v2/admin/organizations',
  authLogin: '/auth/api/auth/login',
  health: '/healthz',
  readiness: '/readyz',
  systemStatus: '/api/v2/system/status',
  deviceInventory: '/api/v2/inventory/devices',
  stations: '/api/v2/inventory/stations',
  /** Manager + device_* roster for Chat UI (same shape as legacy GET /api/agents). */
  stationAgents: (stationId: string) =>
    `/api/v2/inventory/stations/${encodeURIComponent(stationId)}/agents`,
  devices: '/api/v2/inventory/devices',
  /** ChatTest personalities grid — static map by device_type (agent-chat, not inventory DB). */
  devicePersonalities: '/api/v2/agent/device-personalities',
  devicesList: '/api/v2/inventory/devices/list',
  chat: '/api/v2/chat',
  chatDirect: '/api/v2/chat',
  chatMaster: '/api/v2/chat/master',
  chatControl: '/api/v2/chat/control',
  chatAgent: '/api/v2/chat/agent',
  chatEmployee: '/api/v2/chat/employee',
  chatStop: (id: string) => `/api/v2/chat/stop/${id}`,
  chatHistory: '/api/v2/chat/history',
  status: (id: string) => `/api/v2/status/${id}`,
  confirm: (id: string) => `/api/v2/chat/confirm/${id}`,
  confirmVerify: '/api/v2/chat/confirm/verify',
  confirmResend: '/api/v2/chat/confirm/resend',
  command: (cmd: string) => `/api/v2/control/commands/${encodeURIComponent(cmd)}`,
  usage: '/api/v2/usage/session',
  usageHistory: '/api/v2/usage/summary',
  usageLog: '/api/v2/usage/tokens',
  memorySummaryLatest: '/api/v2/chat/memory/summary/latest',
  memorySummaryDaily: '/api/v2/chat/memory/summary/daily',
  memorySummaryPeriodic: '/api/v2/chat/memory/summary/periodic',
  memoryDeviceEvents: (deviceId: string) =>
    `/api/v2/memory/devices/${encodeURIComponent(deviceId)}/events`,
  knowledgeUpload: '/api/v2/knowledge/upload',
  knowledgeDocuments: '/api/v2/knowledge/documents',
  knowledgeDocument: (documentId: string) =>
    `/api/v2/knowledge/documents/${encodeURIComponent(documentId)}`,
  knowledgeSearch: '/api/v2/knowledge/search',
  /** Station rollup from neocortex_summaries (v2 rolling summary + per-device summaries). */
  neocortexRollup: (stationId: string) =>
    `/api/v2/memory/neocortex/station/${encodeURIComponent(stationId)}/rollup?org_id=${encodeURIComponent('{org_id}')}`,
  hippocampusKnowledge: '/api/v2/knowledge/documents',
  taskRuns: '/api/v2/chat/tasks',
  taskRun: (id: string) => `/api/v2/chat/tasks/${id}`,
  kpiSummary: '/api/v2/inventory/kpi/summary',
  guardrails: '/api/v2/admin/guardrails',
  guardrail: (id: number) => `/api/v2/admin/guardrails/${id}`,
  objectLibrary: '/api/object-library',
};

/** Agent Station session login — always same-origin global path (not Limbic direct). */
export function getAsAuthLoginUrl(): string {
  return ENDPOINTS.asAuthLogin;
}

export function getAuthLoginUrl(): string {
  // Locked to the remote Limbic API — never fall back to a relative/localhost origin.
  return AUTH_LOGIN_URL || normalizeAuthLoginUrl(LIMBIC_API_DEFAULT_BASE_URL);
}
